import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  BoldButton,
  ItalicButton,
  UnderlineButton,
} from '@draft-js-plugins/buttons'
import Editor from '@draft-js-plugins/editor'
import createInlineToolbarPlugin from '@draft-js-plugins/inline-toolbar'
import { convertFromHTML, convertToHTML } from 'draft-convert'
import { CompositeDecorator, EditorState, RichUtils } from 'draft-js'
import {
  attempt,
  includes,
  isNil,
  map,
  noop,
  reduce,
  size,
  unescape,
  values,
} from 'lodash'
import PropTypes from 'prop-types'

import { stripHtmlFromText } from '../../utils'
import { StyledRichTextInput } from '../styled'

import ReadMore from './read-more'

import '@draft-js-plugins/inline-toolbar/lib/plugin.css'
import 'draft-js/dist/Draft.css'

const InlineStyleButtons = {
  underline: 'underline',
  italic: 'italic',
  bold: 'bold',
}

const getToolbarButton = externalProps => item => {
  switch (item) {
    case InlineStyleButtons.underline:
      return <UnderlineButton key={`${item}-button`} {...externalProps} />
    case InlineStyleButtons.italic:
      return <ItalicButton key={`${item}-button`} {...externalProps} />
    case InlineStyleButtons.bold:
      return <BoldButton key={`${item}-button`} {...externalProps} />
    default:
      return <span>Not Found</span>
  }
}

const RichTextHighlightingInput = forwardRef(
  (
    {
      value,
      highlights,
      decoratorComponent,
      highlightStrategy,
      maxEditorHeight,
      isDisabled,
      isReadOnly,
      toolbarItems,
      onlySelection,
      onChange,
      onBlur,
      shouldFocusOnMount,
      isFocused,
      setIsFocused,
    },
    ref,
  ) => {
    const isMounting = useRef(true)
    const editorRef = useRef(null)

    const contentState = useMemo(() => convertFromHTML(value), [value])

    const textBlocksWithIndexes = useMemo(
      () =>
        reduce(
          contentState?.getBlocksAsArray(),
          (acc, block, index) => {
            const blockText = block.getText()
            return [
              ...acc,
              {
                text: blockText,
                startIndex: index === 0 ? 0 : acc[index - 1].endIndex + 1,
                endIndex:
                  index === 0
                    ? size(blockText) - 1
                    : acc[index - 1].endIndex + size(blockText),
              },
            ]
          },
          [],
        ),
      [contentState],
    )

    const highlightDecorator = useMemo(
      () =>
        !isNil(highlightStrategy) && !isNil(decoratorComponent)
          ? new CompositeDecorator([
              {
                strategy: highlightStrategy(highlights, textBlocksWithIndexes),
                component: decoratorComponent,
                props: {
                  highlights,
                  textBlocksWithIndexes,
                },
              },
            ])
          : undefined,
      [
        decoratorComponent,
        highlightStrategy,
        highlights,
        textBlocksWithIndexes,
      ],
    )

    const [editorState, setEditorState] = useState(() =>
      EditorState.createWithContent(contentState, highlightDecorator),
    )

    const plugins = useMemo(() => createInlineToolbarPlugin(), [])

    const valueStripedOfHtml = useMemo(() => stripHtmlFromText(value), [value])

    const focusEditor = useCallback(() => {
      editorRef.current.focus()
      setIsFocused(true)
    }, [editorRef, setIsFocused])

    const handleChange = useCallback(
      newEditorState => {
        if (onlySelection) {
          const changedText = unescape(
            convertToHTML(newEditorState.getCurrentContent()),
          )

          const newValueStripedOfHtml = stripHtmlFromText(changedText)
          if (valueStripedOfHtml === newValueStripedOfHtml) {
            setEditorState(newEditorState)
          }
        } else {
          setEditorState(newEditorState)
        }
      },
      [onlySelection, valueStripedOfHtml],
    )

    const handleBlur = useCallback(
      event => {
        const changedText = unescape(
          convertToHTML(editorState.getCurrentContent()),
        )
        if (onlySelection) {
          onChange(changedText)
        } else if (!isMounting.current && value !== changedText) {
          onChange(changedText)
        }

        setIsFocused(false)
        attempt(onBlur, event)
      },
      [editorState, onBlur, onChange, onlySelection, setIsFocused, value],
    )

    useImperativeHandle(ref, () => ({
      blur: () => {
        handleBlur()
      },
    }))

    const handleKeyCommand = useCallback(
      command => {
        const newEditorState = RichUtils.handleKeyCommand(editorState, command)

        if (isNil(newEditorState)) {
          return 'not-handled'
        }

        if (newEditorState && includes(['italic', 'underline'], command)) {
          handleChange(newEditorState)
          return 'handled'
        }

        return 'not-handled'
      },
      [editorState, handleChange],
    )

    useEffect(() => {
      setEditorState(
        EditorState.createWithContent(contentState, highlightDecorator),
      )
    }, [contentState, highlightDecorator])

    useEffect(() => {
      if (shouldFocusOnMount) {
        focusEditor()
      }
    }, [focusEditor, shouldFocusOnMount])

    // needed because the draftjs editor calls on change everytime the editorState changes, even for the first time
    useEffect(() => {
      isMounting.current = false
    }, [])

    const editor = useMemo(
      () => (
        <>
          <Editor
            ref={editorRef}
            readOnly={isDisabled || isReadOnly}
            editorState={editorState}
            onChange={handleChange}
            handleKeyCommand={handleKeyCommand}
            onBlur={handleBlur}
            plugins={toolbarItems && [plugins]}
          />
          {toolbarItems && plugins && (
            <plugins.InlineToolbar>
              {externalProps => (
                <>{map(toolbarItems, getToolbarButton(externalProps))}</>
              )}
            </plugins.InlineToolbar>
          )}
        </>
      ),
      [
        editorState,
        handleBlur,
        handleChange,
        handleKeyCommand,
        isDisabled,
        isReadOnly,
        plugins,
        toolbarItems,
      ],
    )

    return (
      <StyledRichTextInput
        aria-hidden="true"
        onClick={!isDisabled ? focusEditor : undefined}
        className="ant-input textarea"
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        isFocused={isFocused}
      >
        {!isNil(maxEditorHeight) ? (
          <ReadMore component={editor} maxHeight={maxEditorHeight} />
        ) : (
          editor
        )}
      </StyledRichTextInput>
    )
  },
)

RichTextHighlightingInput.propTypes = {
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.string,
  isDisabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  toolbarItems: PropTypes.arrayOf(PropTypes.oneOf(values(InlineStyleButtons))),
  onlySelection: PropTypes.bool,
  decoratorComponent: PropTypes.func,
  highlightStrategy: PropTypes.func,
  highlights: PropTypes.arrayOf(
    PropTypes.shape({
      word: PropTypes.string.isRequired,
      matchType: PropTypes.string,
    }),
  ),
  maxEditorHeight: PropTypes.number,
  shouldFocusOnMount: PropTypes.bool,
  isFocused: PropTypes.bool,
  setIsFocused: PropTypes.func,
}

RichTextHighlightingInput.defaultProps = {
  onBlur: noop,
  onChange: noop,
  highlightStrategy: undefined,
  decoratorComponent: undefined,
  isDisabled: false,
  isReadOnly: false,
  value: '',
  toolbarItems: undefined,
  onlySelection: false,
  highlights: [],
  maxEditorHeight: undefined,
  shouldFocusOnMount: false,
  isFocused: false,
  setIsFocused: noop,
}

export default RichTextHighlightingInput
